import React, { useEffect, useState } from 'react';
import { TrophyIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

interface LeaderboardEntry {
  rank: number;
  userId: string;
  userName: string;
  conversions: number;
  totalEarnings: number;
}

const ReferralLeaderboard: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchLeaderboard();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/api/users/me');
      setCurrentUserId(response.data.id);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/referrals/leaderboard');
      setLeaderboard(response.data);
    } catch (err: any) {
      console.error('Error fetching leaderboard:', err);
      setError(err.response?.data?.error || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <TrophyIcon className="h-8 w-8 text-yellow-500" />
          Referral Leaderboard
        </h1>
        <p className="mt-2 text-gray-600">
          Top referrers this month
        </p>
      </div>

      {/* Top 3 Podium */}
      {leaderboard.length >= 3 && (
        <div className="mb-8 flex items-end justify-center gap-4">
          {/* 2nd Place */}
          <div className="flex-1 max-w-xs">
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-t-lg p-6 text-center border-4 border-gray-300">
              <div className="text-4xl mb-2">🥈</div>
              <p className="font-semibold text-gray-900 text-lg mb-1">
                {leaderboard[1].userName}
              </p>
              <p className="text-sm text-gray-600 mb-2">{leaderboard[1].conversions} conversions</p>
              <p className="text-lg font-bold text-gray-900">
                ${leaderboard[1].totalEarnings.toFixed(2)}
              </p>
            </div>
            <div className="bg-gray-300 h-24 rounded-b-lg"></div>
          </div>

          {/* 1st Place */}
          <div className="flex-1 max-w-xs">
            <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-t-lg p-6 text-center border-4 border-yellow-400">
              <div className="text-5xl mb-2">🥇</div>
              <p className="font-bold text-gray-900 text-xl mb-1">
                {leaderboard[0].userName}
              </p>
              <p className="text-sm text-gray-600 mb-2">{leaderboard[0].conversions} conversions</p>
              <p className="text-2xl font-bold text-yellow-700">
                ${leaderboard[0].totalEarnings.toFixed(2)}
              </p>
            </div>
            <div className="bg-yellow-400 h-32 rounded-b-lg"></div>
          </div>

          {/* 3rd Place */}
          <div className="flex-1 max-w-xs">
            <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-t-lg p-6 text-center border-4 border-orange-300">
              <div className="text-4xl mb-2">🥉</div>
              <p className="font-semibold text-gray-900 text-lg mb-1">
                {leaderboard[2].userName}
              </p>
              <p className="text-sm text-gray-600 mb-2">{leaderboard[2].conversions} conversions</p>
              <p className="text-lg font-bold text-gray-900">
                ${leaderboard[2].totalEarnings.toFixed(2)}
              </p>
            </div>
            <div className="bg-orange-300 h-20 rounded-b-lg"></div>
          </div>
        </div>
      )}

      {/* Full Leaderboard Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ChartBarIcon className="h-6 w-6 text-blue-600" />
            Complete Rankings
          </h2>
        </div>

        {leaderboard.length === 0 ? (
          <div className="p-12 text-center">
            <TrophyIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No referrals yet. Be the first!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Earnings
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaderboard.map((entry) => {
                  const isCurrentUser = entry.userId === currentUserId;
                  const medal = getMedalIcon(entry.rank);

                  return (
                    <tr
                      key={entry.userId}
                      className={`${
                        isCurrentUser ? 'bg-blue-50' : 'hover:bg-gray-50'
                      } transition-colors`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {medal ? (
                            <span className="text-2xl">{medal}</span>
                          ) : (
                            <span className="text-lg font-semibold text-gray-900 w-8 text-center">
                              {entry.rank}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {entry.userName}
                              {isCurrentUser && (
                                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                  You
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {entry.conversions}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-green-600">
                          ${entry.totalEarnings.toFixed(2)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralLeaderboard;
